import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import axios from 'axios'
import { handleJSAPIAccess } from '../../utils/auth_access_util'
import { fileUploadSvc } from '../../utils/FileUploadSvc'
import { fileSvc } from '../../utils/FileSvc'
import { API_CONFIG } from '../../config/apiConfig'
import { showToast, showConfirm } from "../../utils/messageUtil"
import { useUser } from '../../contexts/UserContext'
import './index.css'

export default function MyReservations() {

    const navigate = useNavigate()
    const { userInfo, loading: userLoading, refreshUserInfo } = useUser()
    const [hasRefreshed, setHasRefreshed] = useState(false)

    // 检查用户信息是否完整
    const isUserInfoComplete = () => {
        if (!userInfo) return false
        const hasUserId = userInfo.user_id || userInfo.open_id
        return hasUserId !== ''
    }

    // 如果用户信息缺失，重新获取
    useEffect(() => {
        // 只有在加载完成后、用户信息不完整且还未刷新过时才重新获取
        if (!userLoading && !isUserInfoComplete() && !hasRefreshed) {
            console.log('预约记录页面：用户信息不完整，重新获取...')
            setHasRefreshed(true)
            refreshUserInfo()
        }
    }, [userLoading, userInfo, refreshUserInfo, hasRefreshed])

    const [reservations, setReservations] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [searchKeyword, setSearchKeyword] = useState('')
    
    // 使用对象存储每个预约记录的还车和撤销状态，键为applicationId
    const [returningCarStates, setReturningCarStates] = useState({})
    const [cancellingStates, setCancellingStates] = useState({})
    const [cancelingCarStates, setCancelingCarStates] = useState({})
    const [delayingCarStates, setDelayingCarStates] = useState({})
    const containerRef = useRef(null)
    
    // 还车模态框状态
    const [returnCarModalVisible, setReturnCarModalVisible] = useState(false)
    const [selectedApplicationId, setSelectedApplicationId] = useState(null)
    const [returnImages, setReturnImages] = useState([])
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)

    const getStatusText = (status) => {
        switch (status) {
            case 0:
                return '待车管审批'
            case 1:
                return '待还车'
            case 2:
                return '用车审批驳回'
            case 3:
                return '还车待确认'
            case 4:
                return '已还车'
            case 5:
                return '还车不规范'
            case 6:
                return '取消用车'
            case 7:
                return '待上级审批'
            case 8:
                return '已撤销申请'
            case 9:
                return '上级审批驳回'
            default:
                return '未知状态'
        }
    }

    const getStatusClass = (status) => {
        switch (status) {
            case 0:
            case 3:
            case 7:
                return 'status-pending'
            case 1:
            case 4:
                return 'status-approved'
            case 2:
            case 5:
            case 9:
                return 'status-cancelled'
            case 6:
            case 8:
                return 'status-completed'
            default:
                return ''
        }
    }

    // 获取用户预约数据
    const fetchMyReservations = async (userId, page = 1) => {
        try {
            if (page === 1) {
                setLoading(true)
            } else {
                setLoadingMore(true)
            }
            setError(null)
            // 使用默认用户 ID 进行测试
            const actualUserId = userId
            console.log('使用用户 ID:', actualUserId, '页码:', page)

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getMyCarApplicationList`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                applicantId: actualUserId,
                page: page,
                rows: 10,
                keyword: searchKeyword || undefined
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.success) {
                const reservationData = response.data.result.rows || []
                const total = response.data.result.total || 0
                const records = response.data.result.records || 0
                const rowsPerPage = 10
                const calculatedTotalPages = Math.ceil(records / rowsPerPage)

                setTotalPages(calculatedTotalPages)
                setCurrentPage(page)
                setHasMore(page < calculatedTotalPages)

                if (page === 1) {
                    setReservations(reservationData)
                } else {
                    setReservations(prevReservations => [...prevReservations, ...reservationData])
                }
            } else {
                if (page === 1) {
                    setReservations([])
                }
                setError('获取预约数据失败')
            }
        } catch (error) {
            console.error('获取预约数据失败:', error)
            if (page === 1) {
                setReservations([])
            }
            setError('网络错误，请稍后重试')
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }

    // 当用户信息加载完成后，获取预约数据
    useEffect(() => {
        if (userInfo && userInfo.user_id) {
            fetchMyReservations(userInfo.user_id || userInfo.open_id)
        }
    }, [userInfo])


    // 加载更多
    const handleLoadMore = useCallback(() => {
        if (!loadingMore && hasMore && userInfo) {
            const nextPage = currentPage + 1
            fetchMyReservations(userInfo.user_id || userInfo.open_id, nextPage)
        }
    }, [loadingMore, hasMore, currentPage, userInfo])

    // 上划到底部加载更多
    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current
            if (!container || loadingMore || !hasMore) return

            const { scrollTop, scrollHeight, clientHeight } = container
            // 当滚动到底部附近时触发加载更多
            if (scrollHeight - scrollTop - clientHeight < 50) {
                handleLoadMore()
            }
        }

        const container = containerRef.current
        if (container) {
            container.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll)
            }
        }
    }, [loadingMore, hasMore, handleLoadMore])
    // 撤销申请
    const cancelApplication = async (applicationId, applicantId, instanceCode) => {

        // 防重复点击
        if (cancellingStates[applicationId]) return

        try {
            // 设置当前记录的撤销状态为true
            setCancellingStates(prev => ({
                ...prev,
                [applicationId]: true
            }))
            setError(null)
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/feiShu/instancesCancel`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                applicantId: applicantId,
                instanceCode: instanceCode
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                showToast('撤销成功', 'success');
            } else {
                showToast('撤销失败:' + response.data.message, 'error');
            }
        } catch (error) {
            console.error('撤销失败:', error)
            showToast('网络错误，请稍后重试', 'error');
        } finally {
            // 设置当前记录的撤销状态为false
            setCancellingStates(prev => ({
                ...prev,
                [applicationId]: false
            }))
            // 无论成功失败，都刷新页面
            if (userInfo && userInfo.user_id) {
                fetchMyReservations(userInfo.user_id, 1)
            }
        }
    }
    // 延期还车弹窗状态
    const [delayModalVisible, setDelayModalVisible] = useState(false)
    const [delayApplicationData, setDelayApplicationData] = useState(null)
    const [delayNewEndTime, setDelayNewEndTime] = useState('')
    const [delayReason, setDelayReason] = useState('')

    // 延期还车
    const delayReturnCarApplication = (reservation) => {
        // 打开延期弹窗，默认延期时间为到期时间加1小时
        setDelayApplicationData(reservation)
        setDelayReason('')
        
        // 计算默认延期时间（到期时间 + 1小时）
        const endTime = new Date(reservation.expectedEndTime)
        endTime.setHours(endTime.getHours() + 1)
        // 格式化为 datetime-local 输入框格式 (YYYY-MM-DDTHH:mm)
        const year = endTime.getFullYear()
        const month = String(endTime.getMonth() + 1).padStart(2, '0')
        const day = String(endTime.getDate()).padStart(2, '0')
        const hours = String(endTime.getHours()).padStart(2, '0')
        const minutes = String(endTime.getMinutes()).padStart(2, '0')
        const defaultDelayTime = `${year}-${month}-${day}T${hours}:${minutes}`
        
        setDelayNewEndTime(defaultDelayTime)
        setDelayModalVisible(true)
    }

    // 提交延期申请
    const submitDelayApplication = async () => {
        const reservation = delayApplicationData
        if (!reservation) return

        // 校验
        if (!delayNewEndTime) {
            showToast('请选择延期时间', 'warning');
            return
        }
        if (!delayReason) {
            showToast('请输入延期原因', 'warning');
            return
        }

        // 校验延期时间不能小于申请单到期时间
        const currentEndTime = new Date(reservation.expectedEndTime)
        const newEndTimeDate = new Date(delayNewEndTime)
        if (newEndTimeDate <= currentEndTime) {
            showToast('延期时间必须大于申请单到期时间', 'warning');
            return
        }

        // 校验延期时长不能低于30分钟
        const diffMinutes = (newEndTimeDate - currentEndTime) / (1000 * 60)
        if (diffMinutes < 30) {
            showToast('延期时长不能低于30分钟', 'warning');
            return
        }

        // 防重复点击
        if (delayingCarStates[reservation.applicationId]) return

        try {
            setDelayingCarStates(prev => ({
                ...prev,
                [reservation.applicationId]: true
            }))

            // 转换时间格式为后端需要的格式 yyyy-MM-dd HH:mm:ss
            const formattedEndTime = `${delayNewEndTime.replace('T', ' ')}:00`

            // 获取申请人部门（从申请单中获取）
            const applicantDept = reservation.applicantDept || reservation.department || ''

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/delayReturnCar/submitDelayReturnApplication`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                applicantId: reservation.applicantId,
                applicantName: reservation.applicantName,
                applicantPhone: reservation.applicantPhone,
                applicantDept: applicantDept,
                applicationId: reservation.applicationId,
                newEndTime: formattedEndTime,
                delayReason: delayReason
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                showToast('延期提交成功', 'success');
                setDelayModalVisible(false)
            } else {
                showToast('延期提交失败:' + (response.data?.message || '未知错误'), 'error');
            }
        } catch (error) {
            console.error('延期提交失败:', error)
            showToast('网络错误，请稍后重试', 'error');
        } finally {
            setDelayingCarStates(prev => ({
                ...prev,
                [reservation.applicationId]: false
            }))
            // 无论成功失败，都刷新页面
            if (userInfo && userInfo.user_id) {
                fetchMyReservations(userInfo.user_id, 1)
            }
        }
    }

    // 关闭延期弹窗
    const closeDelayModal = () => {
        setDelayModalVisible(false)
        setDelayApplicationData(null)
        setDelayNewEndTime('')
        setDelayReason('')
    }

    // 取消用车
    const cancelCarApplication = async (applicationId) => {
        // 防重复点击
        if (cancelingCarStates[applicationId]) return;

        try {
            // 设置当前记录的取消状态为true
            setCancelingCarStates(prev => ({
                ...prev,
                [applicationId]: true
            }))
            setError(null)

            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/cancelCarApplication`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                applicationId: applicationId
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                showToast('取消用车成功', 'success');
            } else {
                showToast('取消用车失败:' + response.data.message, 'error');
            }
        } catch (error) {
            console.error('取消用车失败:', error)
            showToast('网络错误，请稍后重试', 'error');
        } finally {
            // 设置当前记录的取消状态为false
            setCancelingCarStates(prev => ({
                ...prev,
                [applicationId]: false
            }))
            // 无论成功失败，都刷新页面
            if (userInfo && userInfo.user_id) {
                fetchMyReservations(userInfo.user_id, 1)
            }
        }
    }

    // 打开还车模态框
    const openReturnCarModal = (applicationId) => {
        setSelectedApplicationId(applicationId)
        setReturnImages([])
        setReturnCarModalVisible(true)
    }

    // 关闭还车模态框
    const closeReturnCarModal = () => {
        setReturnCarModalVisible(false)
        setSelectedApplicationId(null)
        setReturnImages([])
        setUploadProgress(0)
    }

    // 选择图片
    const chooseImages = () => {
        console.log('开始选择图片');

        // 检测平台，PC 端仅支持相册
        const isPC = navigator.userAgent.includes('Windows') || navigator.userAgent.includes('Macintosh');
        const sourceType = isPC ? ['album'] : ['album', 'camera'];

        // 先进行 JSAPI 鉴权，再选择图片
        const handleChooseImage = () => {
            // 在飞书环境中选择图片
            if (typeof fileSvc !== 'undefined' && fileSvc.chooseImage) {
                fileSvc.chooseImage({
                    count: 9,
                    sourceType: sourceType,
                    sizeType: ['compressed']
                }).subscribe(
                    (files) => {
                        console.log('选择图片成功:', files);
                        const newImages = files.map(file => ({
                            path: file.path,
                            size: file.size,
                            uploaded: false,
                            url: '',
                            thumbnail: file.thumbnail || file.path
                        }));
                        setReturnImages(prev => [...prev, ...newImages]);
                    },
                    (error) => {
                    console.log('选择图片失败:', error);
                    // 检查是否是用户取消选择的情况
                    const errorMessage = error.message || '';
                    if (errorMessage.includes('cancel') || errorMessage.includes('用户取消')) {
                        console.log('用户取消选择图片');
                        return; // 不显示错误提示
                    }
                    // PC端如果失败，尝试使用原生文件选择
                    if (isPC) {
                        handleNativeFileSelect();
                    } else {
                        showToast(`选择图片失败: ${error.message}`, 'error');
                    }
                }
                );
            } else {
                // 非飞书环境或chooseImage不可用，使用原生文件选择
                handleNativeFileSelect();
            }
        };

        // 检查是否在飞书环境中
        if (window.h5sdk) {
            console.log('飞书环境，先进行 JSAPI 鉴权');
            // 进行 JSAPI 鉴权
            handleJSAPIAccess((isSuccess) => {
                if (isSuccess) {
                    console.log('JSAPI 鉴权成功，开始选择图片');
                    handleChooseImage();
                } else {
                    console.log('JSAPI 鉴权失败，使用原生文件选择');
                    handleNativeFileSelect();
                }
            });
        } else {
            console.log('非飞书环境，使用原生文件选择');
            handleNativeFileSelect();
        }
    }

    // 使用原生文件选择
    const handleNativeFileSelect = () => {
        // 创建隐藏的文件输入框
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.max = 9;
        input.style.display = 'none';

        input.onchange = (e) => {
            const files = Array.from(e.target.files);
            if (files.length > 0) {
                console.log('原生文件选择成功:', files);
                const newImages = files.map(file => ({
                    path: URL.createObjectURL(file),
                    size: file.size,
                    uploaded: false,
                    url: '',
                    thumbnail: URL.createObjectURL(file),
                    file: file // 保存原始文件对象用于上传
                }));
                setReturnImages(prev => [...prev, ...newImages]);
            }
        };

        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    }

    // 删除图片
    const removeImage = (index) => {
        setReturnImages(prev => prev.filter((_, i) => i !== index));
    }

    // 上传图片到阿里OSS
    const uploadImages = async () => {
        if (returnImages.length === 0) {
            showToast('请至少上传一张图片', 'error');
            return;
        }

        try {
            setUploading(true);
            setUploadProgress(0);

            // 准备上传的文件对象
            const files = returnImages.map(img => {
                // 如果有原始文件对象（来自原生文件选择），直接使用
                if (img.file) {
                    return {
                        path: img.path,
                        size: img.size,
                        name: img.file.name || `image_${Date.now()}.jpg`
                    };
                } else {
                    // 否则从 path 中提取文件名（飞书API返回的情况）
                    return {
                        path: img.path,
                        size: img.size,
                        name: `image_${Date.now()}.${img.path.split('.').pop()}`
                    };
                }
            });

            // 上传图片
            let totalProgress = 0;
            const uploadPromises = files.map((file, index) => {
                return fileUploadSvc.upload(file, 'car_return', {}, (progress) => {
                    // 计算总体进度
                    const currentProgress = Math.round(((index + progress / 100) / files.length) * 100);
                    if (currentProgress > totalProgress) {
                        totalProgress = currentProgress;
                        setUploadProgress(totalProgress);
                    }
                });
            });

            // 等待所有图片上传完成
            const uploadedUrls = await Promise.all(uploadPromises);

            // 更新图片状态
            const uploadedImages = returnImages.map((img, index) => ({
                ...img,
                uploaded: true,
                url: uploadedUrls[index],
                thumbnail: uploadedUrls[index] // 更新 thumbnail 为上传后的 URL
            }));
            setReturnImages(uploadedImages);
            showToast('图片上传成功', 'success');
        } catch (error) {
            console.error('上传图片失败:', error);
            showToast('上传图片失败，请重试', 'error');
        } finally {
            setUploading(false);
        }
    }

    // 提交还车申请
    const submitReturnCar = async () => {
        if (returnImages.length === 0) {
            showToast('请至少上传一张图片', 'error');
            return;
        }

        // 检查是否所有图片都已上传
        const allUploaded = returnImages.every(img => img.uploaded);
        if (!allUploaded) {
            showToast('请等待所有图片上传完成', 'error');
            return;
        }

        if (!selectedApplicationId) return;

        try {
            // 设置当前记录的还车状态为true
            setReturningCarStates(prev => ({
                ...prev,
                [selectedApplicationId]: true
            }));

            // 构建图片URL字符串（用逗号分隔）
            const imageUrls = returnImages.map(img => img.url);
            const returnImagesStr = imageUrls.join(',');

            // 调用还车API
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/return/submit`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                applicationId: selectedApplicationId,
                returnImages: returnImagesStr
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && response.data.code === 200) {
                showToast('还车成功', 'success');
                closeReturnCarModal();
            } else {
                showToast('还车失败:' + response.data.message, 'error');

            }
        } catch (error) {
            console.error('还车失败:', error);
            showToast('网络错误，请稍后重试', 'error');
        } finally {
            // 设置当前记录的还车状态为false
            setReturningCarStates(prev => ({
                ...prev,
                [selectedApplicationId]: false
            }));
            // 无论成功失败，都刷新页面
            if (userInfo && userInfo.user_id) {
                fetchMyReservations(userInfo.user_id, 1);
            }
        }
    }

    // 立即还车
    const handleReturnCar = (applicationId) => {
        openReturnCarModal(applicationId);
    }

    const handleBack = () => {
        navigate('/')
    }

    return (
        <div
            className="my-reservations-page"
            ref={containerRef}
        >
            <h2>预约记录</h2>

            {/* 搜索框 */}
            <div className="search-container" style={{
                marginBottom: '15px',
                padding: '15px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
            }}>
                <input
                    type="text"
                    placeholder="搜索车牌号、申请单号、目的地"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && userInfo && userInfo.user_id) {
                            fetchMyReservations(userInfo.user_id, 1)
                        }
                    }}
                    style={{
                        width: '100%',
                        padding: '10px 15px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                    }}
                />
                <button
                    onClick={() => {
                        if (userInfo && userInfo.user_id) {
                            fetchMyReservations(userInfo.user_id, 1)
                        }
                    }}
                    style={{
                        width: '100%',
                        marginTop: '10px',
                        padding: '10px',
                        backgroundColor: '#007AFF',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    搜索
                </button>
            </div>

            {loading ? (
                <div className="loading-indicator">
                    <p>正在加载预约数据...</p>
                </div>
            ) : error ? (
                <div className="error-message">
                    <p>{error}</p>
                    <button className="retry-btn" onClick={() => {
                        // 重试时清除缓存，重新获取用户信息
                        localStorage.removeItem('userInfo')
                        setError(null)
                        setLoading(true)
                        refreshUserInfo()
                    }}>重试</button>
                </div>
            ) : userLoading ? (
                <div className="loading-indicator">
                    <p>正在加载用户信息...</p>
                </div>
            ) : (
                <div className="reservations-list">
                    {reservations.length === 0 ? (
                        <div className="no-reservations">
                            <p>暂无预约记录</p>
                        </div>
                    ) : (
                        <>
                            {reservations.map(reservation => (
                                <div key={reservation.applicationId} className="reservation-item">
                                    <div className="reservation-header">
                                        <div className="header-content">
                                            <span className="car-number">{reservation.plateNumber}</span>
                                            <div className="status-and-actions">
                                                {/* 状态标签 */}
                                                <span className={`status ${getStatusClass(reservation.approvalStatus)}`}>
                                                    {getStatusText(reservation.approvalStatus)}
                                                </span>
                                                {/* 根据状态和时间判断显示按钮 */}
                                                {(() => {
                                                    // 判断当前时间是否大于用车开始时间
                                                    const currentTime = new Date();
                                                    const startTime = new Date(reservation.expectedStartTime);
                                                    const isAfterStartTime = currentTime >= startTime;

                                                    // 审批状态=1，且当前时间大于用车开始时间，显示立即还车和延期还车按钮
                                                    if (reservation.approvalStatus === 1 && isAfterStartTime) {
                                                        return (
                                                            <>
                                                                <button
                                                                    className="return-car-btn"
                                                                    onClick={() => handleReturnCar(reservation.applicationId)}
                                                                    disabled={returningCarStates[reservation.applicationId]}
                                                                    style={{ marginRight: '8px' }}
                                                                >
                                                                    {returningCarStates[reservation.applicationId] ? '还车中...' : '立即还车'}
                                                                </button>
                                                                <button
                                                                    className="delay-car-btn"
                                                                    onClick={() => delayReturnCarApplication(reservation)}
                                                                    disabled={delayingCarStates[reservation.applicationId]}
                                                                >
                                                                    {delayingCarStates[reservation.applicationId] ? '提交中...' : '延期还车'}
                                                                </button>
                                                            </>
                                                        );
                                                    }
                                                    // 审批状态=1，且当前时间小于用车开始时间，显示取消用车按钮
                                                    else if (reservation.approvalStatus === 1 && !isAfterStartTime) {
                                                        return (
                                                            <button
                                                                className="return-car-btn"
                                                                onClick={async () => {
                                                                    const confirmed = await showConfirm('提示', '取消用车将收回该车辆控车权限，是否确认取消？');
                                                                    if (confirmed) {
                                                                        cancelCarApplication(reservation.applicationId);
                                                                    }
                                                                }}
                                                                disabled={cancelingCarStates[reservation.applicationId]}
                                                            >
                                                                {cancelingCarStates[reservation.applicationId] ? '取消中...' : '取消用车'}
                                                            </button>
                                                        );
                                                    }
                                                    // 审批状态=7，显示撤销申请按钮
                                                    else if (reservation.approvalStatus === 7) {
                                                        return (
                                                            <button
                                                                className="return-car-btn"
                                                                onClick={() => cancelApplication(reservation.applicationId, reservation.applicantId, reservation.instanceCode)}
                                                                disabled={cancellingStates[reservation.applicationId]}
                                                            >
                                                                {cancellingStates[reservation.applicationId] ? '撤销中...' : '撤销申请'}
                                                            </button>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="reservation-details">
                                        <div className="detail-item">
                                            <span className="label">申请单号:</span>
                                            <span className="value">{reservation.applicationNo}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">申请时间:</span>
                                            <span className="value">{reservation.applyTime}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">申请类型:</span>
                                            <span className="value">{reservation.isPersonal === 0 ? '本人申请' : '代人申请'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">用车人:</span>
                                            <span className="value">{reservation.vehicleUserName}</span>
                                        </div>
                                        <div className="detail-item full-width">
                                            <span className="label">用车时间:</span>
                                            <span className="value">{reservation.expectedStartTime} 至 {reservation.expectedEndTime}</span>
                                        </div>

                                        <div className="detail-item">
                                            <span className="label">驾驶方式:</span>
                                            <span className="value">{reservation.driveType === 1 ? '自驾' : '司机代驾'}</span>
                                        </div>
                                        {reservation.driveType === 2 && (
                                            <div className="detail-item">
                                                <span className="label">代驾司机:</span>
                                                <span className="value">{reservation.driverName}</span>
                                            </div>
                                        )}
                                        {reservation.passengers && reservation.passengers.trim() !== '' && (
                                            <div className="detail-item">
                                                <span className="label">同行人:</span>
                                                <span className="value">{reservation.passengers}</span>
                                            </div>
                                        )}
                                        <div className="detail-item">
                                            <span className="label">还车时间:</span>
                                            <span className="value">{reservation.returnApplyTime}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">行驶里程:</span>
                                            <span className="value">{reservation.totalMileage || 0} km</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">用车总费用:</span>
                                            <span className="value">{reservation.amount || 0} 元</span>
                                        </div>
                                        <div className="detail-item full-width">
                                            <span className="label">用车原因:</span>
                                            <span className="value">{reservation.reason}</span>
                                        </div>
                                        <div className="detail-item full-width">
                                            <span className="label">路线:</span>
                                            <span className="value route">{reservation.expectedLocation} → {reservation.destination}</span>
                                        </div>
                                        {/* 还车图片 */}
                                        {reservation.returnImages && (
                                            <div className="detail-item full-width">
                                                <span className="label">还车图片:</span>
                                                <div style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: '8px',
                                                    marginTop: '5px'
                                                }}>
                                                    {/* 处理 returnImages 是字符串的情况 */}
                                                    {(() => {
                                                        // 确保 returnImages 是数组
                                                        let images = [];
                                                        if (Array.isArray(reservation.returnImages)) {
                                                            images = reservation.returnImages;
                                                        } else if (typeof reservation.returnImages === 'string') {
                                                            // 如果是字符串，按逗号分割成数组
                                                            images = reservation.returnImages.split(',').filter(img => img.trim());
                                                        }
                                                        
                                                        // 遍历图片数组
                                                        return images.map((imageUrl, index) => (
                                                            <div key={index} style={{
                                                                position: 'relative',
                                                                width: '80px',
                                                                height: '80px',
                                                                borderRadius: '4px',
                                                                overflow: 'hidden',
                                                                border: '1px solid #ddd',
                                                                cursor: 'pointer'
                                                            }} onClick={() => {
                                                                // 预览图片
                                                                fileSvc.previewImage(images, imageUrl)
                                                                    .then(() => console.log('预览图片成功'))
                                                                    .catch(error => console.log('预览图片失败:', error));
                                                            }}>
                                                                <img 
                                                                    src={imageUrl} 
                                                                    alt={`还车图片 ${index + 1}`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            </div>
                                                        ));
                                                    })()}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loadingMore ? (
                                <div className="loading-more">
                                    <p>加载中...</p>
                                </div>
                            ) : hasMore ? (
                                <div className="load-more-container">
                                    <button className="load-more-btn" onClick={handleLoadMore}>
                                        加载更多
                                    </button>
                                </div>
                            ) : (
                                <div className="no-more-data">
                                    <p>没有更多数据了</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            <div className="action-buttons">
                <button className="back-btn" onClick={handleBack}>返回首页</button>
            </div>
            
            {/* 还车模态框 */}
            {returnCarModalVisible && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '20px',
                        width: '90%',
                        maxWidth: '600px',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{
                            margin: '0 0 20px 0',
                            textAlign: 'center',
                            fontSize: '18px',
                            color: '#333'
                        }}>
                            还车图片上传
                        </h3>
                        
                        {/* 图片上传区域 */}
                        <div style={{
                            marginBottom: '20px'
                        }}>
                            <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '14px',
                                color: '#666'
                            }}>
                                请上传或拍摄车辆当前状态图片，如外观，里程，内饰（至少1张）
                            </h4>
                            
                            {/* 图片预览区域 */}
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '10px',
                                marginBottom: '15px'
                            }}>
                                {returnImages.map((image, index) => (
                                    <div key={index} style={{
                                        position: 'relative',
                                        width: '100px',
                                        height: '100px',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        border: '1px solid #ddd',
                                        cursor: 'pointer'
                                    }} onClick={() => {
                                        // 预览图片
                                        const imageUrls = returnImages.map(img => img.uploaded ? img.url : img.path);
                                        fileSvc.previewImage(imageUrls, image.uploaded ? image.url : image.path)
                                            .then(() => console.log('预览图片成功'))
                                            .catch(error => console.log('预览图片失败:', error));
                                    }}>
                                        <img 
                                            src={image.thumbnail || image.path} 
                                            alt={`图片 ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeImage(index);
                                            }}
                                            style={{
                                                position: 'absolute',
                                                top: '5px',
                                                right: '5px',
                                                backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '20px',
                                                height: '20px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}
                                        >
                                            ×
                                        </button>
                                        {image.uploaded && (
                                            <div style={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                color: 'white',
                                                fontSize: '10px',
                                                padding: '2px',
                                                textAlign: 'center'
                                            }}>
                                                已上传
                                            </div>
                                        )}
                                    </div>
                                ))}
                                
                                {/* 添加图片按钮 */}
                                <button
                                    onClick={chooseImages}
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        borderRadius: '4px',
                                        border: '2px dashed #ddd',
                                        backgroundColor: '#f9f9f9',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        color: '#999'
                                    }}
                                >
                                    <span style={{ fontSize: '24px' }}>+</span>
                                    <span>添加图片</span>
                                </button>
                            </div>
                            
                            {/* 上传按钮 */}
                            <button
                                onClick={uploadImages}
                                disabled={uploading || returnImages.length === 0}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    backgroundColor: '#007AFF',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: uploading || returnImages.length === 0 ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    opacity: uploading || returnImages.length === 0 ? 0.6 : 1
                                }}
                            >
                                {uploading ? '上传中...' : '上传图片'}
                            </button>
                            
                            {/* 上传进度条 */}
                            {uploading && uploadProgress > 0 && (
                                <div style={{
                                    marginTop: '10px',
                                    height: '6px',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '3px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        height: '100%',
                                        backgroundColor: '#007AFF',
                                        width: `${uploadProgress}%`,
                                        transition: 'width 0.3s ease'
                                    }} />
                                </div>
                            )}
                        </div>
                        
                        {/* 按钮区域 */}
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={closeReturnCarModal}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                取消
                            </button>
                            <button
                                onClick={submitReturnCar}
                                disabled={uploading || returnImages.length === 0 || !returnImages.every(img => img.uploaded)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#007AFF',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: uploading || returnImages.length === 0 || !returnImages.every(img => img.uploaded) ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    opacity: uploading || returnImages.length === 0 || !returnImages.every(img => img.uploaded) ? 0.6 : 1
                                }}
                            >
                                确认还车
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 延期还车模态框 */}
            {delayModalVisible && delayApplicationData && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '20px',
                        width: '90%',
                        maxWidth: '500px'
                    }}>
                        <h3 style={{
                            margin: '0 0 20px 0',
                            textAlign: 'center',
                            fontSize: '18px',
                            color: '#333'
                        }}>
                            延期还车申请
                        </h3>

                        {/* 只读信息 */}
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '12px',
                                fontSize: '14px'
                            }}>
                                <span style={{ color: '#666', width: '80px' }}>车牌号:</span>
                                <span style={{ color: '#333', fontWeight: '500' }}>{delayApplicationData.plateNumber}</span>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '12px',
                                fontSize: '14px'
                            }}>
                                <span style={{ color: '#666', width: '80px' }}>申请单号:</span>
                                <span style={{ color: '#333', fontWeight: '500' }}>{delayApplicationData.applicationNo}</span>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '12px',
                                fontSize: '14px'
                            }}>
                                <span style={{ color: '#666', width: '80px' }}>到期时间:</span>
                                <span style={{ color: '#333', fontWeight: '500' }}>{delayApplicationData.expectedEndTime}</span>
                            </div>
                        </div>

                        {/* 延期时间选择 */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontSize: '14px',
                                color: '#666'
                            }}>
                                请设置延期时间 <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="datetime-local"
                                value={delayNewEndTime}
                                onChange={(e) => setDelayNewEndTime(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    fontSize: '14px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* 延期原因 */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontSize: '14px',
                                color: '#666'
                            }}>
                                延期原因 <span style={{ color: 'red' }}>*</span>
                            </label>
                            <textarea
                                value={delayReason}
                                onChange={(e) => setDelayReason(e.target.value)}
                                placeholder="请输入延期原因"
                                rows={3}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    fontSize: '14px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    boxSizing: 'border-box',
                                    resize: 'vertical'
                                }}
                            />
                        </div>

                        {/* 按钮区域 */}
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={closeDelayModal}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                取消
                            </button>
                            <button
                                onClick={submitDelayApplication}
                                disabled={delayingCarStates[delayApplicationData.applicationId]}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#FF9500',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    opacity: delayingCarStates[delayApplicationData.applicationId] ? 0.6 : 1
                                }}
                            >
                                {delayingCarStates[delayApplicationData.applicationId] ? '提交中...' : '提交申请'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}